/**
 * @module VerifyVoIPStrategy
 * @description VoIP verify strategy for agent resolution, payload construction,
 * result normalization, and polling behavior.
 */

import { t } from '@lingui/macro';
import { first, get, isEmpty, some, toNumber } from 'lodash';
import {
    AGENT_RESULT_STATUS,
    AGENT_STATUS_RUNNING,
    NATIVE_TEST,
    QUICK_TEST_TYPE,
} from '../../../verify/VerifySharedConstants';
import {
    POLL_COUNT_MAX,
    POLL_INTERVAL_MS,
    QUICK_TEST_STATUS,
    translateVoipPartyStatus,
    VERIFY_DURATION,
    VERIFY_JITTER,
    VOIP_PARTY_STATUS,
    VOIP_REGISTERED_STATUSES,
} from './VerifyVoIPConstants';
import { AGENT_FIELD_NAME } from '../../../../ServiceTestsConstants';

// Strategy for VoIP (VoipPulseToPulse, VoipPulseToPhone) test types.
// See ServiceTestVerifyHelper.resolveStrategy() for strategy dispatch.

// ---------------------------------------------------------------------------
// Agent resolution
// ---------------------------------------------------------------------------

/**
 * Check if an agent is enabled and has VoIP registration.
 * portal/ui source: verify.service.js isAgentEnabledAndRegistered()
 */
function isAgentEnabledAndRegistered(agent) {
    if (!agent?.voipConfiguration) return false;
    const regStatus = get(agent, 'voipConfiguration.registration.status');
    return regStatus && agent.status === AGENT_STATUS_RUNNING && VOIP_REGISTERED_STATUSES.has(regStatus);
}

/**
 * Get VoIP P2P caller and callee agents from form data and verify they are registered.
 * portal/ui source: verify.service.js buildVoIPP2PVerifyTest() + canAgentsRunP2PTest()
 *
 * @param {Object} formData
 * @param {Array} agents - Available agents (from matchingAgentsRef)
 * @param {Object} dataService - API service for fetching agent details
 * @returns {Promise<{ caller: Object, callee: Object }>}
 */
async function getVoIPP2PAgents(formData, agents, dataService) {
    const callerIds = get(formData, 'testConfig.caller.ids', []);
    const calleeIds = get(formData, 'testConfig.callee.ids', []);
    const callerEntry = callerIds[0];
    const calleeEntry = calleeIds[0];

    if (!callerEntry?.id || !calleeEntry?.id) {
        throw new Error(t`Both nPoints must be configured before the test can be verified.`);
    }

    // Try to find agents in the provided list first, fall back to API fetch
    let callerAgent = (agents || []).find((agent) => agent._id === callerEntry.id);
    let calleeAgent = (agents || []).find((agent) => agent._id === calleeEntry.id);

    // If not found locally, fetch from API (portal/ui uses AgentsService.findById)
    if (!callerAgent || !calleeAgent) {
        try {
            const [fetchedCaller, fetchedCallee] = await Promise.all([
                !callerAgent ? dataService.get(`/ipm/v1/admin/agents/${callerEntry.id}`) : callerAgent,
                !calleeAgent ? dataService.get(`/ipm/v1/admin/agents/${calleeEntry.id}`) : calleeAgent,
            ]);
            callerAgent = callerAgent || fetchedCaller;
            calleeAgent = calleeAgent || fetchedCallee;
        } catch (e) {
            // eslint-disable-line no-unused-vars
            // Fall through to registration check which will throw a clear error
        }
    }

    if (!isAgentEnabledAndRegistered(callerAgent) || !isAgentEnabledAndRegistered(calleeAgent)) {
        throw new Error(t`Both nPoints must be enabled and registered before the test configuration can be verified.`);
    }

    return {
        caller: { ...callerEntry, _id: callerEntry.id },
        callee: { ...calleeEntry, _id: calleeEntry.id },
    };
}

/**
 * Get VoIP Phone agents — find the first enabled+registered agent.
 * portal/ui source: verify.service.js buildVoIPPhoneVerifyTest() + buildVoIPPhoneAgents() + canAgentsRunPhoneTest()
 *
 * @param {Object} formData
 * @param {Array} agents - Available agents
 * @param {Object} dataService
 * @returns {Promise<Object>} Single agent to run the test
 */
async function getVoIPPhoneAgent(formData, agents, dataService) {
    const testAgents = get(formData, AGENT_FIELD_NAME, {});
    let wantedIds;

    if (testAgents.all === false) {
        if (!isEmpty(testAgents.tags)) {
            // Tagged mode: use IDs of agents that matched tags (already in agents list)
            wantedIds = (agents || []).map((agent) => agent._id);
        } else {
            // Selected mode: use the selected IDs
            wantedIds = testAgents.ids || [];
        }
    } else {
        // All mode: use all available agents
        wantedIds = (agents || []).map((agent) => agent._id);
    }

    if (isEmpty(wantedIds)) {
        throw new Error(
            t`There are no nPoints available to verify this test configuration. nPoints must be enabled and registered before the test can be verified.`
        );
    }

    // Fetch each agent to check registration status
    const agentPromises = wantedIds.map((id) => dataService.get(`/ipm/v1/admin/agents/${id}`).catch(() => null));
    const fetchedAgents = await Promise.all(agentPromises);
    const eligible = fetchedAgents.filter(Boolean).filter(isAgentEnabledAndRegistered);

    if (eligible.length === 0) {
        throw new Error(
            t`There are no nPoints available to verify this test configuration. nPoints must be enabled and registered before the test can be verified.`
        );
    }

    return eligible[0];
}

// ---------------------------------------------------------------------------
// Payload builders
// ---------------------------------------------------------------------------

/**
 * Compute sipUrl from callVia (same logic as voipPulseTransform in UseSubmitTransforms).
 * portal/ui computes this via $scope.$watch on callVia.head/body and stores it on
 * test.testConfig.sipUrl, so when verify copies testConfig it's already there.
 * In React, sipUrl is never in the form state — we must derive it here.
 */
function computeSipUrl(testConfig) {
    const head = get(testConfig, 'callVia.head', '');
    const body = get(testConfig, 'callVia.body', '');
    return head || body ? head + body : undefined;
}

/**
 * Build VoIP P2P verify payload.
 * portal/ui source: verify.service.js buildVoIPP2PVerifyTest()
 */
function buildP2PPayload(formData, testType, callerEntry, calleeEntry) {
    const emptyResult = (agentId, agentGuid) => ({
        agent: agentId,
        agentGuid: agentGuid || null,
        events: [],
        results: [],
        status: AGENT_RESULT_STATUS.INITIALIZING,
    });

    const testConfig = formData.testConfig || {};
    const sipUrl = computeSipUrl(testConfig);

    return {
        name: 'Verify P2P',
        type: testType._id,
        quickTestType: QUICK_TEST_TYPE.VOIP_PULSE_TO_PULSE,
        status: QUICK_TEST_STATUS.INITIALIZING,
        beaconPackets: get(formData, 'beaconPackets', false),
        testConfig: {
            ...testConfig,
            duration: testConfig.duration || VERIFY_DURATION,
            jitter: VERIFY_JITTER,
            ...(sipUrl != null && { sipUrl }),
        },
        adapterType: formData.adapterType,
        agents: { all: false, ids: [calleeEntry._id, callerEntry._id] },
        agentResults: [emptyResult(callerEntry._id, callerEntry.guid), emptyResult(calleeEntry._id, calleeEntry.guid)],
    };
}

/**
 * Build VoIP Phone verify payload.
 * portal/ui source: verify.service.js buildVoIPPhoneVerifyTest()
 */
function buildPhonePayload(formData, testType, agent) {
    const emptyResult = (agentId, agentGuid) => ({
        agent: agentId,
        agentGuid: agentGuid || null,
        events: [],
        results: [],
        status: AGENT_RESULT_STATUS.INITIALIZING,
    });

    const testConfig = formData.testConfig || {};
    const sipUrl = computeSipUrl(testConfig);

    return {
        name: 'Verify Phone',
        type: testType._id,
        quickTestType: QUICK_TEST_TYPE.VOIP_PULSE_TO_PHONE,
        status: VOIP_PARTY_STATUS.INITIALIZING,
        beaconPackets: get(formData, 'beaconPackets', false),
        testConfig: {
            ...testConfig,
            duration: testConfig.duration || VERIFY_DURATION,
            jitter: VERIFY_JITTER,
            ...(sipUrl != null && { sipUrl }),
        },
        adapterType: formData.adapterType,
        agents: { all: false, ids: [agent._id] },
        agentResults: [
            emptyResult(agent._id, agent.guid),
            { agent: null, agentGuid: 'phone', events: [], results: [], status: VOIP_PARTY_STATUS.PHONE },
        ],
    };
}

/**
 * Build verify payload — dispatches to P2P or Phone based on test type name.
 * This is async because the pre-check fetches agent details to verify registration.
 *
 * @param {Object} formData
 * @param {Object} testType
 * @param {Array} agents
 * @param {Object} dataService
 * @returns {Promise<Object>} Payload
 */
async function buildVerifyPayload(formData, testType, agents, dataService) {
    const { name: typeName } = testType;

    if (typeName === NATIVE_TEST.VoipPulseToPulse) {
        const { caller, callee } = await getVoIPP2PAgents(formData, agents, dataService);
        return buildP2PPayload(formData, testType, caller, callee);
    }

    if (typeName === NATIVE_TEST.VoipPulseToPhone) {
        const agent = await getVoIPPhoneAgent(formData, agents, dataService);
        return buildPhonePayload(formData, testType, agent);
    }

    throw new Error(t`Unknown VoIP test type: ${typeName}`);
}

// ---------------------------------------------------------------------------
// Result handlers & polling
// ---------------------------------------------------------------------------

/**
 * Check VoIP verify test status.
 * portal/ui source: verifyVoIP.service.js checkStatus()
 * VoIP uses top-level response.status (not agentResults).
 */
function checkStatus(response) {
    const testStatus = get(response, 'status');
    if (!testStatus) return { isSuccessful: false, stillRunning: true };

    return {
        isSuccessful: testStatus === QUICK_TEST_STATUS.COMPLETE,
        stillRunning: !(
            testStatus === QUICK_TEST_STATUS.COMPLETE ||
            testStatus === QUICK_TEST_STATUS.CANCELED ||
            testStatus === QUICK_TEST_STATUS.ERROR
        ),
    };
}

/**
 * Check if VoIP response has terminal results.
 * portal/ui source: verifyVoIP.service.js verifyHasResults()
 */
function verifyHasResults(response) {
    const testStatus = get(response, 'status');
    return (
        testStatus === QUICK_TEST_STATUS.COMPLETE ||
        testStatus === QUICK_TEST_STATUS.CANCELED ||
        testStatus === QUICK_TEST_STATUS.ERROR
    );
}

/**
 * Determine if an agentResult corresponds to the caller.
 * portal/ui source: verifyVoIP.service.js isCaller()
 *
 * IMPORTANT: caller.ids entries are objects {id, guid}, not plain strings.
 * We compare callerEntry.id to agentId, with fallback for plain string entries.
 */
function isCaller(agentId, response) {
    const callerEntry = first(get(response, 'testConfig.caller.ids'));
    return callerEntry?.id === agentId || callerEntry === agentId;
}

/**
 * Build P2P failure status message.
 * portal/ui source: verifyVoIP.service.js buildStatusMessageP2P()
 */
function buildP2PStatusMessage(response) {
    const agentResults = response.agentResults || [];
    if (agentResults.length === 2) {
        const { status: agent1Status } = agentResults[0];
        const { status: agent2Status } = agentResults[1];

        if (agent1Status !== VOIP_PARTY_STATUS.ERROR) {
            const role = isCaller(agentResults[0].agent, response) ? 'Caller' : 'Callee';
            return translateVoipPartyStatus(agent1Status, role);
        }
        if (agent2Status !== VOIP_PARTY_STATUS.ERROR) {
            const role = isCaller(agentResults[1].agent, response) ? 'Caller' : 'Callee';
            return translateVoipPartyStatus(agent2Status, role);
        }
    }
    return t`The test configuration doesn't seem to be valid, both the Caller and the Callee have returned failure.`;
}

/**
 * Build P2Phone failure status message.
 * portal/ui source: verifyVoIP.service.js buildStatusMessageP2Phone()
 */
function buildPhoneStatusMessage(response) {
    const agentResults = response.agentResults || [];
    const activeResult = agentResults.find(
        (agentResult) => agentResult.agent && agentResult.status !== VOIP_PARTY_STATUS.ERROR
    );

    if (activeResult) {
        return translateVoipPartyStatus(activeResult.status, 'Caller');
    }
    return t`The test configuration doesn't seem to be valid, both the Caller and the Callee have returned failure.`;
}

/**
 * Build the verify result object for VoIP tests.
 * portal/ui source: verifyVoIP.service.js buildVerifyResults()
 */
function buildVerifyResult(isSuccessful, response) {
    if (isSuccessful) {
        return {
            status: t`Success`,
            statusMsg: t`The test executed successfully`,
            resultType: 'voip',
            resultData: null,
        };
    }

    const statusMsg =
        response.quickTestType === QUICK_TEST_TYPE.VOIP_PULSE_TO_PHONE
            ? buildPhoneStatusMessage(response)
            : buildP2PStatusMessage(response);

    return {
        status: t`Failure`,
        statusMsg,
        resultType: 'voip',
        resultData: null,
    };
}

/**
 * Dynamic polling counter for VoIP based on ringDuration.
 * portal/ui source: verifyVoIP.service.js pollingCounter()
 */
function pollingCounter(pollState) {
    const agentResults = get(pollState, 'response.agentResults', []);
    const isComplete = some(
        agentResults,
        (agentResult) => agentResult.status === AGENT_RESULT_STATUS.COMPLETE && agentResult.results?.length > 0
    );
    const hasStarted = some(
        agentResults,
        (agentResult) => agentResult.status === AGENT_RESULT_STATUS.RUNNING && (agentResult.results?.length || 0) === 0
    );
    const ringDuration = toNumber(get(pollState, 'response.testConfig.ringDuration', 0));
    const defaultPollCount = ((pollState.testTypeTimeout + ringDuration) * 1000) / POLL_INTERVAL_MS + 10;

    if (isComplete) {
        pollState.pollFor = pollState.pollCount; // eslint-disable-line no-param-reassign
    } else if (hasStarted) {
        if (!pollState.hasStarted) {
            pollState.hasStarted = true; // eslint-disable-line no-param-reassign
            pollState.pollFor = pollState.pollCount + defaultPollCount; // eslint-disable-line no-param-reassign
        }
    } else {
        pollState.pollFor = (POLL_COUNT_MAX * 1000) / POLL_INTERVAL_MS + 10; // eslint-disable-line no-param-reassign
    }

    return pollState;
}

// ---------------------------------------------------------------------------
// Strategy export
// ---------------------------------------------------------------------------

/**
 * @type {import('../../../verify/ServiceTestVerifyHelper').VerifyStrategy}
 */
const verifyVoIPStrategy = {
    buildVerifyPayload,
    checkStatus,
    verifyHasResults,
    buildVerifyResult,
    pollConfig: {
        pollFor: 34,
        pollIntervalMs: POLL_INTERVAL_MS,
    },
    pollingCounter,
    isAsync: true,
};

export default verifyVoIPStrategy;
